import React from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { Text } from '@actual-app/components/text';
import { View } from '@actual-app/components/view';
import { integerToCurrency } from '@actual-app/core/shared/util';

import { cartao, cor, espaco, texto } from '#components/finapp/estilo';
import type { ItemUrgente } from '#components/finapp/motor/filaPendencias';

type Props = {
  itens: ItemUrgente[];
  /**
   * Abre o item (a conta do banco desconectado, por exemplo). Opcional: um
   * item sem destino (`destino: null`, o caso da conta fixa vencida) não
   * vira link, e a faixa continua legível sem nenhuma navegação.
   */
  onAbrir?: (item: ItemUrgente) => void;
};

/** Valor na linha da tabela — ver o mesmo padrão em `inicio/ContasDoMes.tsx`. */
const valorDaLinha = {
  ...texto.corpo,
  fontVariantNumeric: 'tabular-nums' as const,
};

/**
 * Ação em forma de texto: um `<button>` DE VERDADE (acessível por teclado e
 * por leitor de tela), mas sem a caixa de um botão.
 *
 * ⛔ `border: 'none'` não é enfeite — é a regra nº 1 da direção "Extrato":
 * borda repetida em cada linha vira malha, e malha lê-se como aperto.
 */
const acaoDeTexto = {
  ...texto.corpo,
  color: cor.acento,
  cursor: 'pointer',
  background: 'none',
  border: 'none',
  padding: 0,
};

/**
 * Faixa **"Precisa agora"** da tela Pendências (spec §5.1) — o que está
 * QUEBRADO, não o que está só pendente:
 *
 * - **banco desconectado**: no Open Finance (Pluggy) a autorização vence de
 *   tempos em tempos. Enquanto não for renovada o extrato simplesmente para
 *   de chegar — em silêncio. É o item que faz a fila inteira mentir, por
 *   isso vem primeiro;
 * - **conta fixa vencida** sem pagamento encontrado (motor de
 *   `motor/contasFixas.ts` + a data de vencimento; ver `filaPendencias.ts`).
 *
 * ⛔ SEM BORDA e SEM DIVISÓRIA entre linhas — o respiro vem da ALTURA da
 * linha (`espaco.alturaLinha`, 40px), regra nº 1 e nº 3 da direção "Extrato".
 * `cor.saida` marca o que está vencido/falhou: é a única cor além do
 * acento que esta tela usa.
 *
 * Componente BURRO: recebe a lista já pronta e não sabe de onde ela veio
 * (par de `inicio/ContasDoMes.tsx`).
 */
export function PrecisaAgora({ itens, onAbrir }: Props) {
  const { t } = useTranslation();

  if (itens.length === 0) return null;

  return (
    <View style={{ ...cartao, gap: 10 }} data-testid="precisa-agora">
      <Text style={texto.tituloBloco}>
        <Trans>Precisa agora</Trans>
      </Text>

      {itens.map(item => (
        <View
          key={item.id}
          data-testid={`urgente-${item.id}`}
          style={{
            flexDirection: 'row',
            alignItems: 'center',
            height: espaco.alturaLinha,
            gap: 12,
          }}
        >
          <Text style={{ ...texto.corpo, flex: 1 }}>
            <Text aria-hidden style={{ color: cor.saida }}>
              ●
            </Text>{' '}
            {item.tipo === 'banco-desconectado'
              ? t('{{banco}} — reconecte o banco', { banco: item.titulo })
              : t('{{conta}} — venceu e não achei pagamento', {
                  conta: item.titulo,
                })}
          </Text>

          {item.valor !== null && (
            <Text
              data-testid={`urgente-${item.id}-valor`}
              style={{ ...valorDaLinha, color: cor.saida }}
            >
              {integerToCurrency(item.valor)}
            </Text>
          )}

          {item.destino && onAbrir && (
            <button
              type="button"
              data-testid={`urgente-${item.id}-abrir`}
              onClick={() => onAbrir(item)}
              style={{ ...acaoDeTexto, fontWeight: 600 }}
            >
              <Trans>Abrir</Trans>
            </button>
          )}
        </View>
      ))}
    </View>
  );
}
